<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import NucleoWorkOrderRequestConditions from '../components/NucleoWorkOrderRequestConditions.vue';
import NucleoWorkOrderRequestDialog from '../components/NucleoWorkOrderRequestDialog.vue';
import NucleoWorkOrderRequestServices from '../components/NucleoWorkOrderRequestServices.vue';
import { useNucleoWorkOrderRequest } from '../composables/useNucleoWorkOrderRequest';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const transactionId = computed(() => route.params.salesTransactionId);
const state = useNucleoWorkOrderRequest({
  transactionId,
  notify: useAlert,
  t,
});

const goBack = () =>
  router.push({
    name: 'operacao_imobiliaria_pos_venda',
    params: { accountId: route.params.accountId },
    query: route.query,
  });

const submit = async () => {
  if (await state.submit()) goBack();
};

onMounted(state.load);
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <LaisHeader
      :title="t('NUCLEO_WORK_ORDERS.REQUEST.TITLE')"
      :subtitle="t('NUCLEO_WORK_ORDERS.REQUEST.SUBTITLE')"
      :breadcrumb="[
        { label: t('POST_SALE.TITLE') },
        { label: t('NUCLEO_WORK_ORDERS.REQUEST.TITLE') },
      ]"
      show-back
      :back-label="t('NUCLEO_WORK_ORDERS.REQUEST.ACTIONS.BACK')"
      @back="goBack"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="state.loading.value"
          @click="state.load"
        >
          {{ t('NUCLEO_WORK_ORDERS.REQUEST.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <main
      class="mx-auto flex w-full max-w-[1120px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <template v-if="state.loading.value">
        <div
          v-for="index in 3"
          :key="index"
          class="h-36 animate-pulse rounded-lais bg-white"
        />
      </template>
      <template v-else-if="state.catalog.value">
        <LaisCard
          :title="
            state.catalog.value.transaction?.title ||
            t('NUCLEO_WORK_ORDERS.REQUEST.CONTEXT.TITLE')
          "
          prefix-icon="i-lucide-house"
        >
          <p class="text-sm text-lais-text-body">
            {{
              state.catalog.value.transaction?.address ||
              t('NUCLEO_WORK_ORDERS.REQUEST.CONTEXT.ADDRESS')
            }}
          </p>
          <p
            v-if="state.catalog.value.transaction?.reference"
            class="mt-2 text-xs text-lais-text-muted"
          >
            {{ state.catalog.value.transaction.reference }}
          </p>
        </LaisCard>

        <LaisCard
          v-if="!state.configured.value"
          :title="t('NUCLEO_WORK_ORDERS.REQUEST.EMPTY.TITLE')"
          prefix-icon="i-lucide-settings-2"
        >
          <p class="text-sm text-lais-text-body">
            {{ t('NUCLEO_WORK_ORDERS.REQUEST.EMPTY.DESCRIPTION') }}
          </p>
        </LaisCard>

        <template v-else>
          <NucleoWorkOrderRequestServices
            :errors="state.errors.value"
            :form="state.form.value"
            :service-entry="state.serviceEntry"
            :service-types="state.serviceTypes.value"
            @set-count="state.setServiceCount"
            @update-service="state.updateService"
          />
          <NucleoWorkOrderRequestConditions
            v-if="state.form.value.servico.length"
            :errors="state.errors.value"
            :form="state.form.value"
            :sellers="state.sellers.value"
            :states="state.states.value"
            @set-district-count="state.setDistrictCount"
            @update-condition="state.updateCondition"
            @update-district="state.updateDistrict"
          />
          <div class="flex justify-end">
            <LaisPrimaryButton
              icon="i-lucide-arrow-right"
              @click="state.review"
            >
              {{ t('NUCLEO_WORK_ORDERS.REQUEST.ACTIONS.SUBMIT') }}
            </LaisPrimaryButton>
          </div>
        </template>
      </template>
    </main>

    <NucleoWorkOrderRequestDialog
      :show="state.confirmOpen.value"
      :saving="state.saving.value"
      @close="state.confirmOpen.value = false"
      @submit="submit"
    />
  </div>
</template>
